import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../constants';
import { fetchSettings } from '../pages/admin/settings/controller/settings-controller';
import { useBranding } from '../hooks/useBranding';
import { surfaceColors } from '../utils/contrast';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { primaryColor, secondaryColor, storeLogo, shopName, brandingLoaded } = useBranding();

  // The footer uses the boutique's secondary colour, like the header; text and links adapt to stay readable.
  const footer = surfaceColors(secondaryColor || '#fc7303', primaryColor || '#ff4d00');
  const colors = { primary: footer.background };
  const textColor = footer.text;
  const mutedColor = footer.muted;
  const borderColor = footer.border;
  const linkColor = footer.accent;

  // Fetch settings on component mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setIsLoading(true);
        const settingsData = await fetchSettings();
        setSettings(settingsData);
        console.log('Footer settings loaded:', settingsData);
      } catch (error) {
        console.error('Error loading footer settings:', error);
        // No placeholder shop identity: if the API did not answer we leave the
        // contact block empty rather than showing details that aren't the store's.
        setSettings(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Show loading state or fallback if settings not loaded
  if (isLoading) {
    return (
      <footer style={{ backgroundColor: colors.primary, color: textColor }}>
        <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="animate-pulse" style={{ color: mutedColor }}>Chargement...</div>
          </div>
        </div>
      </footer>
    );
  }

  // Only what the backend actually returned — each field is rendered conditionally
  // below, so an unanswered API leaves the slot empty instead of a placeholder brand.
  const companyName = settings?.title || shopName || '';
  const phone = settings?.tel ? `+216 ${settings.tel}` : '';
  const email = settings?.email || '';
  const address = settings?.adresse || '';
  const facebookLink = settings?.lienFacebook;
  const instagramLink = settings?.lienInstagram;

  return (
    <footer style={{ backgroundColor: colors.primary, color: textColor }}>
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Brand and about section */}
          <div className="col-span-1">
            <Link to={ROUTES.home} className="text-xl font-bold flex items-center" style={{ color: textColor }}>
              {brandingLoaded ? (
                <img
                  src={storeLogo || "/logo.jpg"}
                  alt="Brand Logo"
                  className="h-8 w-8 mr-2 rounded-full"
                />
              ) : (
                <span className="h-8 w-8 mr-2 rounded-full bg-gray-200 animate-pulse" aria-hidden="true" />
              )}
              {companyName}
            </Link>
            <p className="mt-4 text-sm" style={{ color: mutedColor }}>
              Des produits de qualité supérieure, un service client irréprochable.
            </p>
            
            {/* Dynamic Social Media Links */}
            <div className="mt-4 flex space-x-4">
              {/* Facebook Link - only show if exists */}
              {facebookLink && (
                <a 
                  href={facebookLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  style={{ color: mutedColor }}
                >
                  <span className="sr-only">Facebook</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                  </svg>
                </a>
              )}
              
              {/* Instagram Link - only show if exists */}
              {instagramLink && (
                <a 
                  href={instagramLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  style={{ color: mutedColor }}
                >
                  <span className="sr-only">Instagram</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                  </svg>
                </a>
              )}
              
              {/* Show placeholder message if no social media links */}
              {!facebookLink && !instagramLink && (
                <p className="text-xs" style={{ color: mutedColor }}>Suivez-nous sur les réseaux sociaux</p>
              )}
            </div>
          </div>

          {/* Dynamic Contact info */}
          <div className="col-span-1">
            <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: textColor }}>Contact</h3>
            <ul className="mt-4 space-y-2">
              {phone && (
                <li style={{ color: mutedColor }} className="flex items-center">
                  <svg className="h-4 w-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {phone}
                </li>
              )}
              {email && (
                <li style={{ color: mutedColor }} className="flex items-center">
                  <svg className="h-4 w-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <a href={`mailto:${email}`} style={{ color: linkColor }}>
                    {email}
                  </a>
                </li>
              )}
              {address && (
                <li style={{ color: mutedColor }} className="flex items-start">
                  <svg className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {address}
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Copyright section */}
      <div 
        style={{ backgroundColor: colors.primary, borderTop: `1px solid ${borderColor}` }}
      >
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-center" style={{ color: mutedColor }}>
            &copy; {currentYear}{companyName ? ` ${companyName}.` : ''} Tous droits réservés.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;